import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { 
  ArrowLeft, ArrowRight, Loader2, Mail, Lock, User, Building2, Phone, 
  Linkedin, Rocket, TrendingUp, GraduationCap, Briefcase, CheckCircle, Shield
} from "lucide-react";

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, signup } = useAuth();
  const { toast } = useToast();
  
  const [isLogin, setIsLogin] = useState(searchParams.get('mode') !== 'signup');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Role selection, 2: Details
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    role: '',
    company_name: '',
    phone: '',
    linkedin_url: ''
  });

  const roles = [
    { 
      id: 'founder', 
      label: 'Founder', 
      icon: Rocket, 
      color: 'green',
      description: 'Apply for accelerator, access mentors & resources',
      features: ['Submit applications', 'Get AI evaluation', 'Access masterclass']
    },
    { 
      id: 'investor', 
      label: 'Investor', 
      icon: TrendingUp, 
      color: 'blue',
      description: 'Access deal flow & governance-ready startups',
      features: ['View portfolio startups', 'Governance scores', 'Investment pipeline']
    },
    { 
      id: 'startup', 
      label: 'Startup', 
      icon: Briefcase, 
      color: 'purple',
      description: 'Track governance, compliance & program progress',
      features: ['Governance dashboard', 'Compliance tracking', 'Mentor matching']
    },
    { 
      id: 'mentor', 
      label: 'Mentor', 
      icon: GraduationCap, 
      color: 'orange',
      description: 'Guide startups & share your expertise',
      features: ['Mentee directory', 'Session scheduling', 'Impact tracking']
    }
  ];

  const handleRoleSelect = (roleId) => {
    setFormData(prev => ({ ...prev, role: roleId }));
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const user = await login(formData.email, formData.password);
        toast({ title: "Welcome back!", description: `Logged in as ${user.full_name}` });
        navigateByRole(user.role);
      } else {
        if (formData.password !== formData.confirmPassword) {
          toast({ title: "Error", description: "Passwords don't match", variant: "destructive" });
          setLoading(false);
          return;
        }
        if (formData.password.length < 8) {
          toast({ title: "Error", description: "Password must be at least 8 characters", variant: "destructive" });
          setLoading(false);
          return;
        }
        
        const user = await signup({
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
          role: formData.role,
          company_name: formData.company_name,
          phone: formData.phone,
          linkedin_url: formData.linkedin_url
        });
        
        // Show success message with email confirmation
        const emailMessage = user.email_sent 
          ? `📧 A welcome email has been sent to ${formData.email}` 
          : `Account created! Check your email at ${formData.email}`;
        
        toast({ 
          title: "🎉 Account created successfully!", 
          description: user.is_approved 
            ? `${emailMessage}. You can now access all features.`
            : `${emailMessage}. Your account is pending admin approval.`
        });
        navigateByRole(user.role);
      }
    } catch (error) {
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "Authentication failed", 
        variant: "destructive" 
      });
    }
    setLoading(false);
  };

  const navigateByRole = (role) => {
    const routes = {
      founder: '/founder-dashboard',
      investor: '/investor-dashboard',
      startup: '/governance',
      mentor: '/mentors',
      admin: '/admin'
    };
    navigate(routes[role] || '/');
  };

  const handleGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const getColorClasses = (color) => {
    const colors = {
      green: { bg: 'bg-green-600', hover: 'hover:bg-green-700', border: 'border-green-500', text: 'text-green-500', light: 'bg-green-500/10' },
      blue: { bg: 'bg-blue-600', hover: 'hover:bg-blue-700', border: 'border-blue-500', text: 'text-blue-500', light: 'bg-blue-500/10' },
      purple: { bg: 'bg-purple-600', hover: 'hover:bg-purple-700', border: 'border-purple-500', text: 'text-purple-500', light: 'bg-purple-500/10' },
      orange: { bg: 'bg-orange-600', hover: 'hover:bg-orange-700', border: 'border-orange-500', text: 'text-orange-500', light: 'bg-orange-500/10' }
    };
    return colors[color] || colors.green;
  };

  return (
    <div className="min-h-screen bg-[#FAF7F0] flex flex-col">
      {/* Header */}
      <header className="backdrop-blur-xl" style={{ background: 'rgba(250,247,240,0.92)', borderBottom: '1px solid rgba(200,168,75,0.2)' }}>
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div onClick={() => navigate('/')} className="flex items-center gap-3 cursor-pointer" data-testid="auth-logo-back">
              <ArrowLeft className="h-4 w-4" style={{ color: '#C8A84B' }} />
              <span style={{ fontFamily: "'Cormorant Display', serif", fontSize: 22, fontWeight: 700, letterSpacing: '.06em', color: '#C8A84B' }}>
                SKI<em style={{ fontStyle: 'italic', fontWeight: 300, color: '#0D1B2A', marginLeft: 2 }}>AXIS</em>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4" style={{ color: '#C8A84B' }} />
              <span style={{ color: '#7A8190', fontSize: 11, letterSpacing: '.18em', textTransform: 'uppercase', fontWeight: 600 }}>Secure Authentication</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-4xl">
          
          {/* Login Form */}
          {isLogin ? (
            <Card className="max-w-md mx-auto" style={{ background: '#FFFFFF', border: '1px solid rgba(200,168,75,0.35)' }}>
              <CardHeader className="text-center" style={{ borderBottom: '1px solid rgba(200,168,75,0.2)', paddingBottom: 24 }}>
                <CardTitle style={{ fontFamily: "'Cormorant Display', serif", fontSize: 36, fontWeight: 700, color: '#0D1B2A', letterSpacing: '-.01em' }}>
                  Welcome <em style={{ fontStyle: 'italic', color: '#C8A84B' }}>back.</em>
                </CardTitle>
                <CardDescription style={{ color: '#3A4A5C', fontSize: 13, letterSpacing: '.04em', marginTop: 8 }}>Sign in to your SKI AXIS account</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label style={{ color: '#0D1B2A', fontSize: 11, fontWeight: 600, letterSpacing: '.14em', textTransform: 'uppercase' }}>Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4" style={{ color: '#7A8190' }} />
                      <Input 
                        type="email" 
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="pl-10" style={{ background: '#FAF7F0', border: '1px solid rgba(200,168,75,0.3)', color: '#0D1B2A' }}
                        placeholder="you@example.com"
                        data-testid="login-email"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label style={{ color: '#0D1B2A', fontSize: 11, fontWeight: 600, letterSpacing: '.14em', textTransform: 'uppercase' }}>Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4" style={{ color: '#7A8190' }} />
                      <Input 
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        className="pl-10" style={{ background: '#FAF7F0', border: '1px solid rgba(200,168,75,0.3)', color: '#0D1B2A' }}
                        placeholder="••••••••"
                        data-testid="login-password"
                        required
                      />
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="w-full"
                    style={{ background: '#C8A84B', color: '#07070A', fontWeight: 800, letterSpacing: '.18em', fontSize: 11, textTransform: 'uppercase', borderRadius: 50, height: 48, border: 'none' }}
                    data-testid="login-submit"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Sign In
                  </Button>
                  
                  {/* Divider */}
                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full" style={{ borderTop: '1px solid rgba(200,168,75,0.3)' }} />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span style={{ padding: '0 12px', background: '#FFFFFF', color: '#7A8190', fontSize: 10, letterSpacing: '.22em', textTransform: 'uppercase', fontWeight: 600 }}>or continue with</span>
                    </div>
                  </div>
                  
                  {/* Google Sign In */}
                  <Button 
                    type="button"
                    onClick={handleGoogleSignIn}
                    className="w-full flex items-center justify-center gap-2"
                    style={{ background: 'transparent', color: '#0D1B2A', border: '1px solid rgba(200,168,75,0.35)', borderRadius: 50, height: 48, fontWeight: 600, fontSize: 13 }}
                    data-testid="google-signin"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </Button>
                </form>
                <div className="mt-6 text-center">
                  <p style={{ color: '#3A4A5C', fontSize: 13 }}>
                    Don't have an account?{' '}
                    <button 
                      onClick={() => { setIsLogin(false); setStep(1); }}
                      style={{ color: '#C8A84B', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 13 }}
                      data-testid="switch-to-signup"
                    >
                      Sign Up →
                    </button>
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Signup Step 1: Role Selection */}
              {step === 1 && (
                <div>
                  <div className="text-center mb-10">
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.3em', textTransform: 'uppercase', color: '#C8A84B', marginBottom: 14 }}>— Join SKI AXIS —</div>
                    <h1 style={{ fontFamily: "'Cormorant Display', serif", fontSize: 48, fontWeight: 700, color: '#0D1B2A', marginBottom: 12, letterSpacing: '-.02em', lineHeight: 1.05 }}>
                      Select your <em style={{ fontStyle: 'italic', color: '#C8A84B' }}>role.</em>
                    </h1>
                    <p style={{ fontFamily: "'Cormorant Display', serif", color: '#3A4A5C', fontSize: 18, fontStyle: 'italic', fontWeight: 300 }}>How will you operate inside the platform?</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {roles.map((role) => (
                      <Card 
                        key={role.id}
                        onClick={() => handleRoleSelect(role.id)}
                        className="cursor-pointer transition-all"
                        style={{
                          background: '#FFFFFF',
                          border: '1px solid rgba(13,27,42,0.08)',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(200,168,75,0.4)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.transform = 'none'; }}
                        data-testid={`role-${role.id}`}
                      >
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div style={{ padding: 12, borderRadius: 12, background: 'rgba(200,168,75,0.07)', border: '1px solid rgba(200,168,75,0.35)' }}>
                              <role.icon className="h-6 w-6" style={{ color: '#C8A84B' }} />
                            </div>
                            <div className="flex-1">
                              <h3 style={{ fontSize: 18, fontWeight: 700, color: '#0D1B2A', marginBottom: 6 }}>{role.label}</h3>
                              <p style={{ fontSize: 13, color: '#3A4A5C', marginBottom: 12, lineHeight: 1.55 }}>{role.description}</p>
                              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {role.features.map((feature, i) => (
                                  <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#0D1B2A' }}>
                                    <CheckCircle className="h-3 w-3" style={{ color: '#C8A84B' }} />
                                    {feature}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  
                  <div className="mt-8 text-center">
                    <p style={{ color: '#3A4A5C', fontSize: 13 }}>
                      Already have an account?{' '}
                      <button 
                        onClick={() => setIsLogin(true)}
                        style={{ color: '#C8A84B', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 13 }}
                        data-testid="switch-to-login"
                      >
                        Sign In →
                      </button>
                    </p>
                  </div>
                </div>
              )}

              {/* Signup Step 2: Details Form */}
              {step === 2 && (
                <Card className="max-w-lg mx-auto" style={{ background: '#FFFFFF', border: '1px solid rgba(200,168,75,0.35)' }}>
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setStep(1)}
                        style={{ color: '#3A4A5C', marginLeft: -8 }}
                      >
                        <ArrowLeft className="h-4 w-4 mr-1" /> Back
                      </Button>
                    </div>
                    <CardTitle style={{ fontFamily: "'Cormorant Display', serif", fontSize: 32, fontWeight: 700, color: '#0D1B2A', letterSpacing: '-.01em' }}>
                      Create your <em style={{ fontStyle: 'italic', color: '#C8A84B' }}>{formData.role}</em> account
                    </CardTitle>
                    <CardDescription style={{ color: '#3A4A5C', fontSize: 13, marginTop: 6 }}>
                      Fill in your details to complete registration
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <Label style={{ color: '#0D1B2A', fontSize: 11, fontWeight: 600, letterSpacing: '.14em', textTransform: 'uppercase' }}>Full Name *</Label>
                          <div className="relative">
                            <User className="absolute left-3 top-3 h-4 w-4" style={{ color: '#7A8190' }} />
                            <Input 
                              value={formData.full_name}
                              onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                              className="pl-10" style={{ background: '#FAF7F0', border: '1px solid rgba(200,168,75,0.3)', color: '#0D1B2A' }}
                              placeholder="John Smith"
                              data-testid="signup-name"
                              required
                            />
                          </div>
                        </div>
                        <div className="md:col-span-2">
                          <Label style={{ color: '#0D1B2A', fontSize: 11, fontWeight: 600, letterSpacing: '.14em', textTransform: 'uppercase' }}>Email *</Label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-3 h-4 w-4" style={{ color: '#7A8190' }} />
                            <Input 
                              type="email"
                              value={formData.email}
                              onChange={(e) => setFormData({...formData, email: e.target.value})}
                              className="pl-10" style={{ background: '#FAF7F0', border: '1px solid rgba(200,168,75,0.3)', color: '#0D1B2A' }}
                              placeholder="you@example.com"
                              data-testid="signup-email"
                              required
                            />
                          </div>
                        </div>
                        <div>
                          <Label style={{ color: '#0D1B2A', fontSize: 11, fontWeight: 600, letterSpacing: '.14em', textTransform: 'uppercase' }}>Password *</Label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-3 h-4 w-4" style={{ color: '#7A8190' }} />
                            <Input 
                              type="password"
                              value={formData.password}
                              onChange={(e) => setFormData({...formData, password: e.target.value})}
                              className="pl-10" style={{ background: '#FAF7F0', border: '1px solid rgba(200,168,75,0.3)', color: '#0D1B2A' }}
                              placeholder="Min 8 characters"
                              data-testid="signup-password"
                              required
                            />
                          </div>
                        </div>
                        <div>
                          <Label style={{ color: '#0D1B2A', fontSize: 11, fontWeight: 600, letterSpacing: '.14em', textTransform: 'uppercase' }}>Confirm Password *</Label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-3 h-4 w-4" style={{ color: '#7A8190' }} />
                            <Input 
                              type="password"
                              value={formData.confirmPassword}
                              onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                              className="pl-10" style={{ background: '#FAF7F0', border: '1px solid rgba(200,168,75,0.3)', color: '#0D1B2A' }}
                              placeholder="Repeat password"
                              data-testid="signup-confirm-password"
                              required
                            />
                          </div>
                        </div>
                        <div>
                          <Label style={{ color: '#0D1B2A', fontSize: 11, fontWeight: 600, letterSpacing: '.14em', textTransform: 'uppercase' }}>Company/Organization</Label>
                          <div className="relative">
                            <Building2 className="absolute left-3 top-3 h-4 w-4" style={{ color: '#7A8190' }} />
                            <Input 
                              value={formData.company_name}
                              onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                              className="pl-10" style={{ background: '#FAF7F0', border: '1px solid rgba(200,168,75,0.3)', color: '#0D1B2A' }}
                              placeholder="Your company"
                              data-testid="signup-company"
                            />
                          </div>
                        </div>
                        <div>
                          <Label style={{ color: '#0D1B2A', fontSize: 11, fontWeight: 600, letterSpacing: '.14em', textTransform: 'uppercase' }}>Phone</Label>
                          <div className="relative">
                            <Phone className="absolute left-3 top-3 h-4 w-4" style={{ color: '#7A8190' }} />
                            <Input 
                              value={formData.phone}
                              onChange={(e) => setFormData({...formData, phone: e.target.value})}
                              className="pl-10" style={{ background: '#FAF7F0', border: '1px solid rgba(200,168,75,0.3)', color: '#0D1B2A' }}
                              placeholder="+971 50 123 4567"
                              data-testid="signup-phone"
                            />
                          </div>
                        </div>
                        <div className="md:col-span-2">
                          <Label style={{ color: '#0D1B2A', fontSize: 11, fontWeight: 600, letterSpacing: '.14em', textTransform: 'uppercase' }}>LinkedIn Profile</Label>
                          <div className="relative">
                            <Linkedin className="absolute left-3 top-3 h-4 w-4" style={{ color: '#7A8190' }} />
                            <Input 
                              value={formData.linkedin_url}
                              onChange={(e) => setFormData({...formData, linkedin_url: e.target.value})}
                              className="pl-10" style={{ background: '#FAF7F0', border: '1px solid rgba(200,168,75,0.3)', color: '#0D1B2A' }}
                              placeholder="linkedin.com/in/yourprofile"
                              data-testid="signup-linkedin"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <Button 
                        type="submit" 
                        disabled={loading}
                        className="w-full"
                        style={{ background: '#C8A84B', color: '#07070A', fontWeight: 800, letterSpacing: '.18em', fontSize: 12, textTransform: 'uppercase', borderRadius: 50, height: 56, border: 'none' }}
                        data-testid="signup-submit"
                      >
                        {loading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : null}
                        Create Account
                      </Button>
                      
                      <p style={{ fontSize: 11, color: '#7A8190', textAlign: 'center', letterSpacing: '.04em' }}>
                        By signing up, you agree to our Terms of Service and Privacy Policy
                      </p>
                    </form>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
